import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  Nfc, 
  Copy, 
  Check, 
  Smartphone, 
  CreditCard,
  ExternalLink,
  ChevronRight,
  Info,
  ShoppingCart,
  ArrowLeft,
  QrCode
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NFCSetupPage = () => {
  const { cardId } = useParams();
  const { getAuthHeaders } = useAuth();
  const [nfcData, setNfcData] = useState(null);
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [nfcRes, cardRes] = await Promise.all([
          axios.get(`${API_URL}/api/cards/${cardId}/nfc-data`, { headers: getAuthHeaders() }),
          axios.get(`${API_URL}/api/cards/${cardId}`, { headers: getAuthHeaders() })
        ]);
        setNfcData(nfcRes.data);
        setCard(cardRes.data);
      } catch (error) {
        toast.error('Failed to load NFC data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [cardId, getAuthHeaders]);

  const copyToClipboard = async () => {
    if (nfcData?.url) {
      await navigator.clipboard.writeText(nfcData.url);
      setCopied(true);
      toast.success('URL copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const recommendedApps = [
    { name: 'NFC Tools', platform: 'iOS & Android', link: 'https://www.wakdev.com/en/apps/nfc-tools.html' },
    { name: 'NXP TagWriter', platform: 'Android', link: 'https://play.google.com/store/apps/details?id=com.nxp.nfc.tagwriter' },
    { name: 'Simply NFC', platform: 'iOS', link: 'https://apps.apple.com/app/simply-nfc-tag-writer-reader/id1187658907' }
  ];

  const recommendedCards = [
    { name: 'NTAG215', capacity: '504 bytes', best_for: 'Most use cases' },
    { name: 'NTAG216', capacity: '888 bytes', best_for: 'Longer URLs' },
    { name: 'NTAG213', capacity: '144 bytes', best_for: 'Short URLs only' }
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="loading-spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">NFC Setup</h1>
              <p className="text-muted-foreground">
                Program an NFC card to share {card?.name}'s contact with a tap
              </p>
            </div>
          </div>
          <Badge className="bg-[#00A78B] text-white">
            <Nfc className="w-3 h-3 mr-1" />
            NFC Ready
          </Badge>
        </div>

        {/* URL Card */}
        <Card className="border-2 border-[#00A78B]/20 bg-gradient-to-br from-[#00A78B]/5 to-transparent">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Nfc className="w-5 h-5 text-[#00A78B]" />
              Your NFC URL
            </CardTitle>
            <CardDescription>
              This URL will be programmed onto your NFC card
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input 
                value={nfcData?.url || ''} 
                readOnly 
                className="font-mono text-sm bg-background"
                data-testid="nfc-url-input"
              />
              <Button 
                onClick={copyToClipboard}
                variant={copied ? "default" : "outline"}
                className={copied ? "bg-[#00A78B] hover:bg-[#018F76]" : ""}
                data-testid="copy-nfc-url-btn"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy
                  </>
                )}
              </Button>
            </div>
            
            <div className="flex gap-2">
              <a 
                href={nfcData?.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex-1"
              >
                <Button variant="outline" className="w-full gap-2">
                  <ExternalLink className="w-4 h-4" />
                  Preview Card
                </Button>
              </a>
              <Link to={`/cards/${cardId}/qr-customizer`} className="flex-1">
                <Button variant="outline" className="w-full gap-2">
                  <QrCode className="w-4 h-4" />
                  QR Code
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* How to Program */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-[#2E7FC1]" />
              How to Program Your NFC Card
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {nfcData?.instructions?.map((instruction, index) => (
                <div key={index} className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#00A78B] text-white flex items-center justify-center font-bold text-sm">
                    {index + 1}
                  </div>
                  <div className="flex-1 pt-1">
                    <p className="text-foreground">{instruction}</p>
                  </div>
                </div>
              ))}
              
              <Separator />
              
              <div className="space-y-4">
                <h4 className="font-semibold flex items-center gap-2">
                  <Info className="w-4 h-4 text-[#00A78B]" />
                  Detailed Steps
                </h4>
                <ol className="space-y-3 text-muted-foreground">
                  <li className="flex gap-3">
                    <span className="text-[#00A78B] font-medium">1.</span>
                    <span>Download an NFC writing app on your smartphone (see recommendations below)</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#00A78B] font-medium">2.</span>
                    <span>Open the app and select "Write" or "Write URL"</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#00A78B] font-medium">3.</span>
                    <span>Click "Copy" above to copy your unique card URL</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#00A78B] font-medium">4.</span>
                    <span>Paste the URL into the NFC writer app</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#00A78B] font-medium">5.</span>
                    <span>Hold your NFC card against the back of your phone</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#00A78B] font-medium">6.</span>
                    <span>Wait for the "Write successful" confirmation</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#00A78B] font-medium">7.</span>
                    <span>Test by tapping the card with any NFC-enabled phone</span>
                  </li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recommended Apps */}
        <Card>
          <CardHeader>
            <CardTitle>Recommended NFC Writing Apps</CardTitle>
            <CardDescription>Free apps to program your NFC cards</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              {recommendedApps.map((app) => (
                <a 
                  key={app.name}
                  href={app.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold">{app.name}</h4>
                          <p className="text-sm text-muted-foreground">{app.platform}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </CardContent>
                  </Card>
                </a>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recommended Cards */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-[#2E7FC1]" />
              Recommended NFC Cards
            </CardTitle>
            <CardDescription>Compatible NFC card types for your digital business card</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              {recommendedCards.map((nfcCard) => (
                <Card key={nfcCard.name} className="bg-muted/30">
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-lg">{nfcCard.name}</h4>
                    <p className="text-sm text-muted-foreground">Capacity: {nfcCard.capacity}</p>
                    <Badge variant="secondary" className="mt-2">{nfcCard.best_for}</Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              You can purchase NFC cards from Amazon, AliExpress, or specialty NFC suppliers. 
              Look for blank NFC cards or stickers with NTAG chips.
            </p>
          </CardContent>
        </Card>

        {/* Order Physical Cards CTA */}
        <Card className="bg-gradient-to-r from-[#16335E] to-[#0A1A34] text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                  <ShoppingCart className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold">Want Pre-Programmed Cards?</h3>
                  <p className="text-white/80">Order professional NFC business cards with your design</p>
                </div>
              </div>
              <Link to={`/cards/${cardId}/order`}>
                <Button className="bg-[#00A78B] hover:bg-[#018F76] text-white" data-testid="order-cards-btn">
                  Order Cards
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default NFCSetupPage;
